import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import { Editor, Extension } from '@tiptap/core';
import Blockquote from '@tiptap/extension-blockquote';
import Bold from '@tiptap/extension-bold';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import BulletList from '@tiptap/extension-bullet-list';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import DropCursor from '@tiptap/extension-dropcursor';
import GapCursor from '@tiptap/extension-gapcursor';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import History from '@tiptap/extension-history';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import { Placeholder } from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import Text from '@tiptap/extension-text';
import { Suggestion, SuggestionOptions } from '@tiptap/suggestion';
import { TextSelection } from 'prosemirror-state';
import tippy, { Instance } from 'tippy.js';
import { Markdown } from 'tiptap-markdown';

import { Upload } from '../../../../models';
import { ApiService } from '../../../../services/api.service';
import { MessageService } from '../../../../services/message.service';
import { UploadService } from '../../../../services/upload.service';
import { CommandSuggestionListComponent } from '../../../components/command-suggestion-list/command-suggestion-list.component';
import { EmojiPickerComponent } from '../../../components/emoji-picker/emoji-picker.component';
import { ImageModalComponent } from '../../../components/image-modal/image-modal.component';
import { TextPopoverComponent } from '../../../components/text-popover/text-popover.component';
import { AuthImage } from '../../../tiptap-extensions/auth-image';
import { CustomDocument } from '../../../tiptap-extensions/custom-document';
import { DocumentDiff, getCleanContent } from '../../../tiptap-extensions/document-diff';
import { DraggableBlock } from '../../../tiptap-extensions/draggable-block';
import { IndividualDiffControls } from '../../../tiptap-extensions/individual-diff-controls';
import { TrailingNode } from '../../../tiptap-extensions/trailing-node';

interface CommandItem {
  title: string;
  icon: string;
  action: ({ editor }: { editor: Editor }) => void;
}

const baseSuggestionOptions: Omit<Partial<SuggestionOptions<CommandItem>>, 'items' | 'render'> = {
  char: '/',
  allowSpaces: false,
  startOfLine: false,
  command: ({ editor, range, props }) => {
    // Calculate the correct range: from the start of the trigger char
    // to the current cursor position (which should be after the query)
    const currentPos = editor.state.selection.$from.pos;
    const correctRange = { from: range.from, to: currentPos };

    // Use the calculated correct range for deletion
    // Add a check for safety in case positions are weird
    if (correctRange.from < correctRange.to) {
      editor.chain().deleteRange(correctRange).focus().run();
      props.action({ editor });
    } else {
      // Fallback if range calculation failed, just run action
      console.warn('Could not determine correct range for suggestion deletion, only running action.');
      props.action({ editor });
    }
  },
  allow: ({ editor, range, state }) => {
    return true;
  },
};

const SlashCommandsExtension = Extension.create<{
  suggestion: Partial<SuggestionOptions<any>>;
}>({
  name: 'slashCommands',
  addProseMirrorPlugins() {
    if (!this.options.suggestion) return [];
    const suggestionPlugin = Suggestion({
      editor: this.editor,
      ...this.options.suggestion,
    });
    return [suggestionPlugin];
  },
});

@Component({
  selector: 'app-knowledge-base-editor',
  templateUrl: './knowledge-base-editor.component.html',
  styleUrls: ['./knowledge-base-editor.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule],
})
export class KnowledgeBaseEditorComponent implements OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('editorContainer') private editorContainer: ElementRef;
  @Output() focusTitle = new EventEmitter<void>();

  @Input() initialContent: string = '';

  @Input() placeholder: string = 'Start writing or type / to see commands...';
  @Input() editable: boolean = true;
  @Input() enableLinkEditing: boolean = true;
  @Input() showToolbar: boolean = true;
  @Input() diffMode: boolean = false;
  @Input() comparisonContent: string = '';
  @Input() trackChangesEnabled: boolean = true;
  @Input() suggestionsMode: boolean = false;

  @Output() outstandingDiffsChange = new EventEmitter<boolean>();

  editor: Editor;
  private outstandingDiffs: boolean = false;

  constructor(
    private elementRef: ElementRef,
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    private viewContainerRef: ViewContainerRef,
    private uploadService: UploadService,
    private messageService: MessageService,
    private apiService: ApiService,
    private httpClient: HttpClient,
    private auth: Auth,
  ) {}

  ngAfterViewInit() {
    this.initEditor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editable'] && this.editor) {
      const isEditable = changes['editable'].currentValue;
      if (this.editor.isEditable !== isEditable) {
        this.editor.setEditable(isEditable);
      }
    }
  }

  private initEditor() {
    const suggestionOptions: Partial<SuggestionOptions<CommandItem>> = {
      ...baseSuggestionOptions,
      items: ({ query }) => this.getSlashCommands(query),
      render: this.createSuggestionRenderer(),
    };

    // Define Extensions Array Explicitly
    const extensions: Extension[] = [
      // --- CORE STRUCTURE ---
      CustomDocument, // Force DraggableBlock at top level
      DraggableBlock() as any,
      Text,
      Paragraph,

      // --- UTILITIES ---
      DropCursor.configure({
        width: 2,
        color: '#777',
        class: 'unthread-dropcursor',
      }),
      GapCursor,
      History,
      HardBreak,

      // --- MARKS ---
      Bold,
      Italic,
      Strike,
      Link.configure({
        linkOnPaste: true,
        autolink: false,
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),

      // --- NODES ---
      AuthImage.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'inline-image',
        },
        httpClient: this.httpClient,
        auth: this.auth,
      }),

      // --- OTHER BLOCK NODES (Allowed *within* DraggableBlock) ---
      ListItem,
      BulletList,
      OrderedList,
      Blockquote,
      Code,
      CodeBlock,
      Heading.configure({ levels: [1, 2, 3] }),
      TrailingNode.configure(),

      // --- CUSTOM FUNCTIONALITY & UI ---
      Markdown.extend({
        name: 'customMarkdown',

        addOptions() {
          return {
            ...this.parent?.(),
            // Just use the same options as tiptap-markdown, but force breaks: true
            breaks: true,
          };
        },
      }),
      DocumentDiff,
      Placeholder.configure({
        placeholder: () => this.placeholder,
        includeChildren: true,
      }),
      BubbleMenu.configure({
        pluginKey: 'formattingBubbleMenu',
        element: document.querySelector('.formatting-bubble-menu'),
        tippyOptions: {
          duration: 100,
          appendTo: () => this.elementRef.nativeElement,
          delay: [300, 0],
          placement: 'top',
          maxWidth: 'none',
        },
        shouldShow: ({ editor, state, from, to }) => {
          const { selection } = state;
          // Only show for non-empty TEXT selections when editor is editable
          return editor.isEditable && selection instanceof TextSelection && !selection.empty;
        },
      }),
      BubbleMenu.extend({ name: 'linkBubbleMenu' }).configure({
        pluginKey: 'linkContextMenu',
        element: document.querySelector('.link-context-menu'),
        tippyOptions: {
          duration: 100,
          appendTo: () => this.elementRef.nativeElement,
          placement: 'bottom',
          maxWidth: 'none',
        },
        shouldShow: ({ editor, state }) => {
          const { selection } = state;
          return editor.isEditable && selection.empty && editor.isActive('link');
        },
      }),
      SlashCommandsExtension.configure({ suggestion: suggestionOptions }),
      IndividualDiffControls.configure({
        onAcceptChange: (pos: number, mark: any) => this.handleIndividualDiffChange(),
        onRejectChange: (pos: number, mark: any) => this.handleIndividualDiffChange(),
      }),
    ];
    // --- End Extensions Array ---

    // Run editor creation outside Angular zone to prevent init-related change detection issues
    this.editor = new Editor({
      element: this.editorContainer?.nativeElement,
      editable: this.editable,
      content: this.initialContent || '',
      onUpdate: this.handleEditorUpdate,
      extensions,
      editorProps: {
        handleKeyDown: (view, event) => {
          const { state } = view;
          const { selection, doc, tr, schema } = state;
          const { $head } = selection;

          // ArrowUp to focus title logic
          if (event.key === 'ArrowUp' && selection.empty) {
            if ($head.parentOffset === 0 && $head.index(0) === 0 && $head.index(1) === 0) {
              event.preventDefault();
              this.focusTitle.emit();
              return true;
            }
          }

          // Ctrl+K for link logic
          if ((event.metaKey || event.ctrlKey) && event.key === 'k' && this.enableLinkEditing) {
            event.preventDefault();
            const { from, to } = selection;
            if (from !== to) {
              this.addLink();
            } else {
              console.warn('Select text before pressing Cmd/Ctrl+K to add a link.');
            }
            return true;
          }

          return false; // Allow Tiptap extensions to process
        },
        handleDrop: (view, event, slice, moved) => {
          if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length) {
            const files = Array.from(event.dataTransfer.files);
            const imageFiles = files.filter((file) => file.type.startsWith('image/'));

            if (imageFiles.length > 0) {
              event.preventDefault();
              this.handleImageDrop(imageFiles);
              return true;
            }
          }
          return false;
        },
        handlePaste: (view, event, slice) => {
          const items = Array.from(event.clipboardData?.items || []);
          const imageItems = items.filter((item) => item.type.startsWith('image/'));

          if (imageItems.length > 0) {
            event.preventDefault();
            this.handleImagePaste(imageItems);
            return true;
          }
          return false;
        },
      },
    });
  }

  private handleEditorUpdate = ({ editor }) => {
    // This callback is still triggered on editor updates,
    // but we no longer emit contentChange automatically.

    // Check for outstanding diffs when content updates
    this.updateOutstandingDiffsStatus();
  };

  /**
   * Returns content as Markdown.
   */
  public getContent(): string | null {
    if (this.editor) {
      // If we're in diff mode (comparison content is set), get clean content
      if (this.comparisonContent) {
        return getCleanContent(this.editor);
      }

      // Use our custom markdown method that preserves empty lines
      if (this.editor.storage.customMarkdown?.getMarkdown) {
        return this.editor.storage.customMarkdown.getMarkdown();
      }

      // Fallback to standard markdown if custom method not available
      if (this.editor.storage.markdown) {
        const markdown = this.editor.storage.markdown.getMarkdown();
        return markdown;
      }
    }
    console.warn('[getContent] Editor not ready or markdown extension missing when getContent was called.');
    return null;
  }

  /**
   * Allows parent component to programmatically set editor content AFTER init.
   */
  public setContent(newContent: string): void {
    if (this.editor) {
      // Avoid emitting update if setting content programmatically
      this.editor.commands.setContent(newContent, false);
    }
  }

  public focus(position: 'start' | 'end' | 'all' | number | null = null): void {
    this.editor?.commands.focus(position);
  }

  private async showLinkPopover(isEdit: boolean = false) {
    if (!this.enableLinkEditing) return;
    let initialValue = '';
    if (isEdit && this.editor) {
      initialValue = this.editor.getAttributes('link').href || '';
    }
    const popover = await this.popoverCtrl.create({
      component: TextPopoverComponent,
      componentProps: {
        title: isEdit ? 'Edit link' : 'Add link',
        placeholder: 'https://',
        autoFocus: true,
        text: initialValue,
        shouldSelect: !isEdit && !initialValue,
        showSaveButton: true,
        saveOnEnter: true,
        callback: async (href: string) => {
          if (!href || href.trim() === '') {
            this.unsetLink();
            return;
          }
          let finalHref = href.trim();
          if (
            !finalHref.startsWith('http://') &&
            !finalHref.startsWith('https://') &&
            !finalHref.startsWith('mailto:')
          ) {
            finalHref = `https://${finalHref}`;
          }
          this.editor.chain().focus().extendMarkRange('link').setLink({ href: finalHref }).run();
        },
      },
      showBackdrop: true,
      backdropDismiss: true,
    });
    await popover.present();
  }

  addLink() {
    const { empty } = this.editor.state.selection;
    if (empty) {
      console.warn('Select text before adding a link.');
      return;
    }
    this.showLinkPopover(false);
  }

  editLink() {
    this.showLinkPopover(true);
  }

  unsetLink() {
    this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
  }

  openLink() {
    if (this.editor) {
      const url = this.getSelectedLink();
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        console.warn('Could not find URL for the current link mark.');
      }
    }
  }

  getSelectedLink() {
    return this.editor?.getAttributes('link').href;
  }

  public clearContent(): void {
    this.editor?.commands.clearContent(true);
  }

  ngOnDestroy() {
    this.editor?.destroy();
  }

  // --- Toolbar Actions ---
  toggleBold(): void {
    this.editor?.chain().focus().toggleBold().run();
  }
  toggleItalic(): void {
    this.editor?.chain().focus().toggleItalic().run();
  }
  toggleStrike(): void {
    this.editor?.chain().focus().toggleStrike().run();
  }
  toggleBulletList(): void {
    this.editor?.chain().focus().toggleBulletList().run();
  }
  toggleOrderedList(): void {
    this.editor?.chain().focus().toggleOrderedList().run();
  }
  toggleBlockquote(): void {
    this.editor?.chain().focus().toggleBlockquote().run();
  }
  toggleCode(): void {
    this.editor?.chain().focus().toggleCode().run();
  }
  toggleCodeBlock(): void {
    this.editor?.chain().focus().toggleCodeBlock().run();
  }
  setHeading(level: 1 | 2 | 3): void {
    this.editor?.chain().focus().toggleHeading({ level }).run();
  }
  clearFormatting(): void {
    if (!this.editor) return;

    // Clear all marks (bold, italic, etc.)
    this.editor.chain().focus().unsetAllMarks().run();

    // Reset block type to plain paragraph
    this.editor.chain().focus().setParagraph().run();
  }
  async insertEmoji(event?: MouseEvent) {
    const previousSelection = this.editor?.state.selection;

    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: (emoji: any) => {
          popover.dismiss({ selectedEmoji: emoji });
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event: event,
    });

    // Workaround for re-acquiring focus
    popover.onDidDismiss().then(({ data }) => {
      if (data?.selectedEmoji?.native && this.editor) {
        this.editor.chain().focus().insertContent(data.selectedEmoji.native).run();
      } else if (previousSelection && this.editor) {
        this.editor.chain().focus().setTextSelection(previousSelection).run();
      }
    });

    await popover.present();
  }

  async insertImage() {
    const modal = await this.modalCtrl.create({
      component: ImageModalComponent,
      componentProps: {
        searchValue: '',
        showStockPhotos: false,
      },
      showBackdrop: true,
      backdropDismiss: true,
    });

    await modal.present();

    const { data } = await modal.onWillDismiss();

    if (!data || !data.uploadFile) {
      return;
    }

    try {
      // Upload the image
      await this.uploadService.upload([data.uploadFile], 'knowledge-base');

      // Insert the image into the editor
      this.editor
        ?.chain()
        .focus()
        .setImage({ src: data.uploadFile.url, alt: data.fileName || 'image' })
        .run();
    } catch (error) {
      console.error('Error uploading image:', error);
      this.messageService.show('Failed to upload image. Please try again.');
    }
  }

  async insertImageFromUrl() {
    const popover = await this.popoverCtrl.create({
      component: TextPopoverComponent,
      componentProps: {
        title: 'Enter Image URL',
        placeholder: 'https://example.com/image.jpg',
        autoFocus: true,
        text: '',
        shouldSelect: true,
        showSaveButton: true,
        saveOnEnter: true,
        callback: async (url: string) => {
          if (!url || url.trim() === '') {
            return;
          }
          const imageUrl = url.trim();
          this.editor?.chain().focus().setImage({ src: imageUrl, alt: 'image' }).run();
        },
      },
      showBackdrop: false,
      backdropDismiss: true,
    });
    await popover.present();
  }

  removeImage() {
    this.editor?.chain().focus().deleteSelection().run();
  }

  private async handleImageDrop(files: File[]) {
    for (const file of files) {
      await this.uploadAndInsertImage(file);
    }
  }

  private async handleImagePaste(items: DataTransferItem[]) {
    for (const item of items) {
      const file = item.getAsFile();
      if (file) {
        await this.uploadAndInsertImage(file);
      }
    }
  }

  private async uploadAndInsertImage(file: File) {
    try {
      const currentUpload = new Upload(file);
      await this.uploadService.upload([currentUpload], 'knowledge-base');
      this.editor?.chain().focus().setImage({ src: currentUpload.url, alt: file.name }).run();
    } catch (error) {
      console.error('Error uploading image:', error);
      this.messageService.show('Failed to upload image. Please try again.');
    }
  }

  // --- Suggestion Renderer (Corrected) ---
  private createSuggestionRenderer(): SuggestionOptions['render'] {
    return () => {
      let componentRef: ComponentRef<CommandSuggestionListComponent> | null = null;
      let tippyInstance: Instance | null = null;
      return {
        onStart: (props) => {
          if (!this.viewContainerRef) {
            console.error('[Suggestion onStart] Missing viewContainerRef');
            return;
          }
          try {
            componentRef = this.viewContainerRef.createComponent(CommandSuggestionListComponent);
            const instance = componentRef.instance;
            instance.items = props.items;
            instance.updateSelectedIndex(0);
            instance.itemSelected.subscribe((item) => {
              props.command(item);
            });
            componentRef.changeDetectorRef.detectChanges();
            const element = componentRef.location.nativeElement as HTMLElement;
            if (!(element instanceof HTMLElement)) {
              console.error('[Suggestion onStart] Component element not HTMLElement');
              componentRef?.destroy();
              componentRef = null;
              return;
            }
            tippyInstance = tippy('body', {
              getReferenceClientRect: props.clientRect,
              appendTo: this.elementRef.nativeElement,
              content: element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
              theme: 'suggestion-popup',
              arrow: false,
              popperOptions: {
                strategy: 'fixed',
                modifiers: [{ name: 'flip', enabled: false }],
              },
            })[0];
            if (tippyInstance) {
              tippyInstance.show();
            } else {
              console.error('[Suggestion onStart] Tippy creation failed.');
              componentRef?.destroy();
              componentRef = null;
            }
          } catch (e) {
            console.error('Error during suggestion onStart:', e);
            componentRef?.destroy();
            componentRef = null;
            tippyInstance?.destroy();
            tippyInstance = null;
          }
        },
        onUpdate: (props) => {
          if (!componentRef?.instance || !tippyInstance || tippyInstance.state.isDestroyed) {
            return;
          }
          componentRef.instance.items = props.items;
          componentRef.instance.updateSelectedIndex(0);
          componentRef.changeDetectorRef.detectChanges();
          tippyInstance.setProps({ getReferenceClientRect: props.clientRect });
        },
        onKeyDown: (props) => {
          if (!componentRef?.instance || !tippyInstance) return false;
          const instance = componentRef.instance;
          const event = props.event;
          if (event.key === 'ArrowUp') {
            const newIndex = (instance.selectedIndex - 1 + instance.items.length) % instance.items.length;
            instance.updateSelectedIndex(newIndex);
            return true;
          }
          if (event.key === 'ArrowDown') {
            const newIndex = (instance.selectedIndex + 1) % instance.items.length;
            instance.updateSelectedIndex(newIndex);
            return true;
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            instance.triggerSelectedItem();
            return true;
          }
          if (event.key === 'Escape') {
            if (tippyInstance && !tippyInstance.state.isDestroyed) {
              tippyInstance.hide();
            }
            return true;
          }
          return false;
        },
        onExit: () => {
          if (tippyInstance && !tippyInstance.state.isDestroyed) {
            tippyInstance.destroy();
          }
          componentRef?.destroy();
          componentRef = null;
          tippyInstance = null;
        },
      };
    };
  }

  // --- Slash Commands (Adjusted) ---
  private getSlashCommands(query: string): CommandItem[] {
    const commands: CommandItem[] = [
      {
        title: 'Emoji',
        icon: 'happy-outline',
        action: ({ editor }) => {
          this.insertEmoji();
        },
      },
      {
        title: 'Bold',
        icon: 'custom-bold',
        action: ({ editor }) => editor.chain().focus().toggleBold().run(),
      },
      {
        title: 'Italic',
        icon: 'custom-italic',
        action: ({ editor }) => editor.chain().focus().toggleItalic().run(),
      },
      {
        title: 'Strikethrough',
        icon: 'custom-strikethrough',
        action: ({ editor }) => editor.chain().focus().toggleStrike().run(),
      },
      {
        title: 'Heading 1',
        icon: 'custom-heading-1',
        action: ({ editor }) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      },
      {
        title: 'Heading 2',
        icon: 'custom-heading-2',
        action: ({ editor }) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      },
      {
        title: 'Heading 3',
        icon: 'custom-heading-3',
        action: ({ editor }) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      },
      {
        title: 'Bullet List',
        icon: 'custom-unordered-list',
        action: ({ editor }) => editor.chain().focus().toggleBulletList().run(),
      },
      {
        title: 'Numbered List',
        icon: 'custom-ordered-list',
        action: ({ editor }) => editor.chain().focus().toggleOrderedList().run(),
      },
      {
        title: 'Blockquote',
        icon: 'custom-blockquote',
        action: ({ editor }) => editor.chain().focus().toggleBlockquote().run(),
      },
      {
        title: 'Inline Code',
        icon: 'custom-code-view',
        action: ({ editor }) => editor.chain().focus().toggleCode().run(),
      },
      {
        title: 'Code Block',
        icon: 'custom-code-block',
        action: ({ editor }) => editor.chain().focus().toggleCodeBlock().run(),
      },
      {
        title: 'Link',
        icon: 'custom-link',
        action: ({ editor }) => {
          if (editor.state.selection.empty) {
            console.warn('Select text before using /link command');
          }
          this.showLinkPopover(false);
        },
      },
      {
        title: 'Image',
        icon: 'image-outline',
        action: ({ editor }) => {
          this.insertImage();
        },
      },
      {
        title: 'Image from URL',
        icon: 'link-outline',
        action: ({ editor }) => {
          this.insertImageFromUrl();
        },
      },
      {
        title: 'Clear formatting',
        icon: 'custom-format-clear',
        action: ({ editor }) => {
          this.editor.chain().focus().unsetAllMarks().setParagraph().run();
        },
      },
    ];
    let filteredCommands = commands;
    if (query && query.trim() !== '') {
      filteredCommands = commands.filter((item) => item.title.toLowerCase().startsWith(query.toLowerCase()));
    }
    return filteredCommands;
  }

  // Document Diff Methods (simplified - no real-time tracking)
  showDocumentDiff(originalContent: string, modifiedContent: string): void {
    this.editor?.commands.showDocumentDiff({
      originalContent,
      modifiedContent,
    });
  }

  clearDocumentDiff(): void {
    this.editor?.commands.clearDocumentDiff();
  }

  // These methods are kept for interface compatibility but do nothing
  // since we're not using real-time track changes
  acceptChange(): void {
    console.warn('Real-time track changes not enabled. Use document diff instead.');
  }

  rejectChange(): void {
    console.warn('Real-time track changes not enabled. Use document diff instead.');
  }

  acceptAllChanges(): void {
    if (!this.editor) return;

    this.editor.commands.acceptAllChanges();
    this.updateOutstandingDiffsStatus();
  }

  rejectAllChanges(): void {
    if (!this.editor) return;

    this.editor.commands.rejectAllChanges();
    this.updateOutstandingDiffsStatus();
  }

  toggleTrackChanges(): void {
    this.trackChangesEnabled = !this.trackChangesEnabled;
  }

  enableSuggestionsMode(): void {
    this.suggestionsMode = true;
    this.trackChangesEnabled = true;
  }

  disableSuggestionsMode(): void {
    this.suggestionsMode = false;
    this.trackChangesEnabled = false;
  }

  // Check for outstanding diffs
  public hasOutstandingDiffs(): boolean {
    if (!this.editor) return false;

    let hasDiffs = false;
    this.editor.state.doc.descendants((node) => {
      if (node.isText && node.marks.some((mark) => mark.type.name === 'insertion' || mark.type.name === 'deletion')) {
        hasDiffs = true;
        return false; // Stop iteration
      }
    });

    return hasDiffs;
  }

  // Handle individual diff changes and update outstanding status
  private handleIndividualDiffChange(): void {
    // Use setTimeout to ensure the change has been applied
    setTimeout(() => {
      const hasOutstanding = this.hasOutstandingDiffs();
      if (this.outstandingDiffs !== hasOutstanding) {
        this.outstandingDiffs = hasOutstanding;
        this.outstandingDiffsChange.emit(hasOutstanding);
      }
    }, 10);
  }

  // Update outstanding diffs status when content changes
  private updateOutstandingDiffsStatus(): void {
    // Use setTimeout to defer the emission until after change detection
    setTimeout(() => {
      const hasOutstanding = this.hasOutstandingDiffs();
      if (this.outstandingDiffs !== hasOutstanding) {
        this.outstandingDiffs = hasOutstanding;
        this.outstandingDiffsChange.emit(hasOutstanding);
      }
    }, 0);
  }
}
